import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ContextFullness } from '@voice-assistant/shared';

// ════════════════════════════════════════════════════════════════════════════
// contextFullnessStore — honest context-meter state (Phase 0b of
// plans/kimi-tool-parity.md, #1976; contract §12).
//
// Origin-keyed by sessionId, this store HOLDS the server-computed
// ContextFullness union — it performs NO arithmetic: the measured union is
// resolved by the backend ledger (resolveContextFullness) and the projected
// value arrives pre-computed from the server (chars/4 of VA-appended tool
// results). The store only orders/discards:
//   • observeMidTurn is monotone non-decreasing within an inter-anchor gap —
//     a projected value BELOW the current one for the SAME anchor is ignored,
//     so the pill never ticks backwards between anchors.
//   • A fresh reconcileFromTurn measured anchor DISCARDS the projection.
//   • The projection never mutates the measured anchor — the projected entry
//     carries `anchorTokens` (the last measured value) through unchanged.
//   • Persist rehydrate demotes projected → pending: a projection is mid-turn
//     display state, never settled truth, so it must never survive a reload.
//
// SE-COVERS: setpoint-evals/context-fullness/SE-12-midturn-movement-and-projection-bounds
// SE-COVERS: setpoint-evals/context-fullness/SE-13-store-fsm-and-transitions
// ════════════════════════════════════════════════════════════════════════════

const STORE_KEY = 'va-context-fullness-v1';

interface ContextFullnessState {
  bySession: Record<string, ContextFullness>;
}

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
// Wildcard-from rows: the store performs no arithmetic, so the target kind
// depends ONLY on the action's server-computed payload, never on the source
// kind — the full 5-from × target cartesian compresses to these 8 rows
// (derivation: setpoint-evals/context-fullness/SE-13-store-fsm-and-transitions/README.md).
// `pending` and `projected` are never server-delivered (pending is the
// client-side initial/reset kind; projected is mid-turn only), so the
// reconcile actions have exactly three targets each.

export const TRANSITIONS: Array<{ from: string; to: string; trigger: string }> = [
  { from: '*', to: 'measured', trigger: 'reconcileFromTurn' },
  { from: '*', to: 'unknown-window', trigger: 'reconcileFromTurn' },
  { from: '*', to: 'unmetered', trigger: 'reconcileFromTurn' },
  { from: '*', to: 'measured', trigger: 'reconcileFromLoad' },
  { from: '*', to: 'unknown-window', trigger: 'reconcileFromLoad' },
  { from: '*', to: 'unmetered', trigger: 'reconcileFromLoad' },
  { from: '*', to: 'projected', trigger: 'observeMidTurn' },
  { from: '*', to: 'pending', trigger: 'clearForSession' },
];

// ─── Rehydrate sanitize (projected → pending demotion) ─────────────────────────
// Shared by migrate (version bumps) and merge (every rehydrate) so a blob at
// ANY version gets the same treatment: a persisted projected entry reads as
// pending after reload, measured/unknown-window/unmetered/pending survive.

function sanitizeBySession(persisted: unknown): Record<string, ContextFullness> {
  const src = (persisted ?? {}) as { bySession?: Record<string, ContextFullness> };
  const clean: Record<string, ContextFullness> = {};
  for (const [sid, f] of Object.entries(src.bySession ?? {})) {
    if (!f || typeof f !== 'object' || !('kind' in f)) continue;
    clean[sid] = f.kind === 'projected' ? { kind: 'pending' } : f;
  }
  return clean;
}

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useContextFullnessStore = create<ContextFullnessState>()(
  persist(
    (): ContextFullnessState => ({ bySession: {} }),
    {
      name: STORE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ bySession: state.bySession }),
      migrate: (persisted) => ({ bySession: sanitizeBySession(persisted) }),
      merge: (persisted, current) => ({ ...current, bySession: sanitizeBySession(persisted) }),
    },
  ),
);

// ─── Actions (one named action per intent — R-A2; exactly these four) ──────────

function setFullness(sessionId: string, fullness: ContextFullness): void {
  useContextFullnessStore.setState((s) => ({
    bySession: { ...s.bySession, [sessionId]: fullness },
  }));
}

/** Turn-end settle (SESSION_HEALTH_DATA): the server's ledger-resolved union
 *  is the new truth — a fresh measured anchor DISCARDS any projection. */
export function reconcileFromTurn(sessionId: string, fullness: ContextFullness): void {
  if (fullness.kind === 'pending' || fullness.kind === 'projected') return; // never server-settled kinds
  setFullness(sessionId, fullness);
}

/** Connect/load settle (MESSAGES_LOADED): the persisted occupancy pair
 *  re-resolved through the ledger — pending when the session never measured. */
export function reconcileFromLoad(sessionId: string, fullness: ContextFullness): void {
  if (fullness.kind === 'pending' || fullness.kind === 'projected') return;
  setFullness(sessionId, fullness);
}

/** Mid-turn projection overlay (TOOL_ACTIVITY type:'context'). Display-only:
 *  monotone non-decreasing within an inter-anchor gap (a value below the
 *  current one for the SAME anchor is ignored); a NEW anchor resets the gap.
 *  Never mutates the measured anchor — the entry carries anchorTokens through. */
export function observeMidTurn(sessionId: string, fullness: ContextFullness): void {
  if (fullness.kind !== 'projected') return;
  const cur = useContextFullnessStore.getState().bySession[sessionId];
  const curAnchor =
    cur?.kind === 'projected' ? cur.anchorTokens : cur?.kind === 'measured' ? cur.contextTokens : undefined;
  const curTokens = cur?.kind === 'projected' || cur?.kind === 'measured' ? cur.contextTokens : undefined;
  if (curAnchor !== undefined && curAnchor === fullness.anchorTokens && curTokens !== undefined && fullness.contextTokens < curTokens) {
    return; // regression inside the same inter-anchor gap — the pill never ticks backwards
  }
  setFullness(sessionId, fullness);
}

/** Reset a session's entry to pending (session deleted / context reset). */
export function clearForSession(sessionId: string): void {
  setFullness(sessionId, { kind: 'pending' });
}
